import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Transition } from '../../components/Transition';
import { useAppDispatch, useAppSelector } from '../../store';
import { useFormik } from 'formik';
import { createChangeUserProfileStatus } from '../../store/changeUserProfileStatus/api';
import { Attachments, Users } from '../../store/users/api/interfaces';
// import { setStatus as setUsersStatus } from '../../store/users/usersSlice';
import { isValidArray } from '../../utils/valid';
// import Iconify from '../../components/Iconify';
import { getAttachmentAssetUrl, getAttachmentPreviewSrc } from '../../utils/url';
import { useEffect, useState } from 'react';
import { createChangeUserProfileStatusSchema } from '../../validation/changeUserProfileStatus';
import { findUser } from '../../store/users/api';
import ImageViewer from '../../components/ImageViewer';
import { config } from '../../utils/utils';
import axios from 'axios';

type RejectionReasonOption = {
  id: string;
  en: string;
  ar: string;
};
// import {
//   boxImgPdfSX,
//   boxImgSX,
//   boxImgStaticSX,
// } from '../../utils/viewAssetsStyle';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  actionUser: Users | undefined;
  search?: string | undefined;
  sortBy?: string | undefined;
  sortByOrder?: string | undefined;
  onRefresh?: () => void;
}

interface IInitialState {
  userId: string;
  isApproved: boolean;
  rejectionReason?: string;
  tradeLicenseExpirationDate?: Date | null;
  brokerLicenseExpirationDate?: Date | null;
}

const UsersActionModal: React.FC<IProps> = ({
  toggle,
  onClose,
  actionUser,
  search,
  sortBy,
  sortByOrder,
  onRefresh,
}) => {
  const dispatch = useAppDispatch();
  const userId = actionUser?.id ?? '';
  const isApproved = actionUser?.isApproved ?? false;
  const [hasSelectedRadio, setHasSelectedRadio] = useState(false);
  const [rejectionReasonOptions, setRejectionReasonOptions] = useState<
    RejectionReasonOption[]
  >([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>([]);

  const [openLightBox, setOpenLightBox] = useState(false);
  const [activeLightBoxIndex, setActiveLightBoxIndex] = useState<number>(0);
  const [attachments, setAttachments] = useState<Attachments[]>([]);
  const { limit, skip } = useAppSelector((state) => state.user);
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  useEffect(() => {
    if (actionUser) {
      if (actionUser?.attachments?.length > 0) {
        setAttachments(actionUser?.attachments ?? []);
      }
    }
  }, [actionUser?.attachments]);

  useEffect(() => {
    const loadRejectionReasons = async () => {
      if (!accessToken || !toggle) return;
      try {
        const response = await axios.get(
          `${config.BASE_URL}/registration-rejection-reasons`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: {
              activeState: true,
              $limit: 100,
            },
          }
        );
        setRejectionReasonOptions(response?.data?.data || []);
      } catch (e) {
        setRejectionReasonOptions([]);
      }
    };
    loadRejectionReasons();
  }, [accessToken, toggle]);

  const appendSelectedReasonsToText = (
    currentText: string,
    nextSelectedIds: string[],
    options: RejectionReasonOption[]
  ) => {
    const selectedBlocks = nextSelectedIds
      .map((id) => {
        const option = options.find((item) => item.id === id);
        if (!option) return '';
        return `${option.en}\n${option.ar}`;
      })
      .filter(Boolean);
    const freeText = (currentText || '')
      .split(/\n{2,}/)
      .filter((block) => {
        return !options.some(
          (option) =>
            block.includes(option.en) || block.includes(option.ar)
        );
      })
      .join('\n\n')
      .trim();
    return [selectedBlocks.join('\n\n'), freeText].filter(Boolean).join('\n\n');
  };

  const handleImageClick = (index: number) => {
    setActiveLightBoxIndex(index);
    setOpenLightBox(true);
  };

  const handleNextImage = () => {
    setActiveLightBoxIndex((prev) => 
      (prev + 1) % (attachments?.length || 1)
    );
  };

  const handlePreviousImage = () => {
    setActiveLightBoxIndex((prev) => 
      prev === 0 ? (attachments?.length - 1 || 0) : prev - 1
    );
  };

  const {
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    values,
  } = useFormik<IInitialState>({
    initialValues: {
      userId,
      isApproved,
      rejectionReason: '',
      tradeLicenseExpirationDate: null,
      brokerLicenseExpirationDate: null,
    },
    validationSchema: createChangeUserProfileStatusSchema(actionUser?.userRole),
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError }) => {
      try {
        console.log('Form submission started', { values, userRole: actionUser?.userRole });
        
        // Validate expiration date is provided when approving
        if (values.isApproved) {
          if (actionUser?.userRole === 'broker' && !values.brokerLicenseExpirationDate) {
            // Show error and prevent submission
            setFieldError('brokerLicenseExpirationDate', 'Broker License Expiration Date is required');
            console.error('Broker license expiration date is required');
            return;
          } else if (actionUser?.userRole === 'company' && !values.tradeLicenseExpirationDate) {
            // Show error and prevent submission
            setFieldError('tradeLicenseExpirationDate', 'Company License Expiration Date is required');
            console.error('Company license expiration date is required');
            return;
          }
        }

        // Format expiration date to ISO string if provided (only when approving)
        const formattedValues: any = {
          userId: values.userId,
          isApproved: values.isApproved,
          rejectionReason: values.rejectionReason || undefined,
        };
        
        // Only include expiration date if approving - use the correct field based on user role
        if (values.isApproved) {
          if (actionUser?.userRole === 'company' && values.tradeLicenseExpirationDate) {
            formattedValues.tradeLicenseExpirationDate = values.tradeLicenseExpirationDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          } else if (actionUser?.userRole === 'broker' && values.brokerLicenseExpirationDate) {
            formattedValues.brokerLicenseExpirationDate = values.brokerLicenseExpirationDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          }
        }
        
        console.log('Calling API with:', formattedValues);
        await dispatch(
          createChangeUserProfileStatus(formattedValues)
        ).unwrap();
        console.log('API call successful');
        
        // Close modal and reset form first
        onClose();
        resetForm();
        setHasSelectedRadio(false);
        
        // Trigger parent refresh callback to refresh the list
        if (onRefresh) {
          // Small delay to ensure modal closes first, then refresh
          setTimeout(() => {
            onRefresh();
          }, 100);
        } else {
          // Fallback: refresh directly if no callback provided
          await dispatch(
            findUser({
              limit,
              skip,
              ...(search && { search: search }),
              ...(sortBy && { sortByName: sortBy }),
              ...(sortByOrder && { sortByOrder: sortByOrder }),
            })
          ).unwrap();
        }
        // dispatch(setUsersStatus({ status: 'idle' }));
      } catch (error: any) {
        // Error is already handled by toastHandler in the API
      }
    },
  });

  return (
    <>
      <Dialog
        fullWidth
        open={toggle}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          resetForm();
          setHasSelectedRadio(false);
          onClose();
        }}
        aria-describedby='alert-dialog-slide-description'
        key={`uf`}
      >
        <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
          {actionUser?.name}
        </DialogTitle>

        <form
          onSubmit={handleSubmit}
          autoComplete='off'
        >
          <DialogContent dividers>
            {isValidArray(actionUser?.attachments) && (
              <>
                <Box
                  sx={{
                    whiteSpace: 'normal',
                    m: 2,
                    maxHeight: 150,
                    overflow: 'auto',
                    display: 'flex',
                  }}
                >
                  {actionUser?.attachments?.map((attachment, index) => {
                    if (attachment.fileType.split('/')[0] === 'image') {
                      return (
                        <div
                          key={attachment.key}
                          style={{
                            width: '100px',
                            height: '100px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            margin: '8px',
                            position: 'relative',
                            flexShrink: '0',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImageClick(index)}
                        >
                          <Tooltip
                            title={attachment?.fileName}
                            key={attachment?.id}
                          >
                            <img
                              id={attachment.key}
                              alt={attachment?.fileName}
                              src={getAttachmentPreviewSrc(attachment, accessToken)}
                              crossOrigin={'anonymous'}
                              loading='lazy'
                              style={{
                                width: '100px',
                                height: '100px',
                                maxWidth: '100%',
                                borderRadius: 'unset',
                                position: 'relative',
                              }}
                            />
                          </Tooltip>
                        </div>
                      );
                    } else if (
                      attachment?.fileType?.split('/')[0] === 'application' &&
                      attachment?.fileType?.split('/')[1] === 'pdf'
                    ) {
                      return (
                        <div
                          key={attachment?.id}
                          style={{
                            width: '110px',
                            height: '110px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            margin: '8px',
                            position: 'relative',
                            flexShrink: '0',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImageClick(index)}
                        >
                          <Tooltip
                            title={attachment?.fileName}
                            key={attachment?.id}
                          >
                            <a
                              href={getAttachmentAssetUrl(attachment, accessToken)}
                              target='_blank'
                              rel='noopener noreferrer'
                              style={{ textDecoration: 'none' }}
                            >
                              <img
                                src='/static/pdf.png'
                                style={{
                                  width: '110px',
                                  height: '110px',
                                  maxWidth: '100%',
                                  borderRadius: 'unset',
                                  position: 'relative',
                                }}
                              />
                            </a>
                          </Tooltip>
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={attachment?.id}
                          style={{
                            width: '100px',
                            height: '100px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            margin: '8px',
                            position: 'relative',
                            flexShrink: '0',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImageClick(index)}
                        >
                          <Tooltip
                            title={attachment?.fileName}
                            key={attachment?.id}
                          >
                            <img
                              alt={attachment?.fileName}
                              src='/static/docket.png'
                              style={{
                                width: '100px',
                                height: '100px',
                                maxWidth: '100%',
                                borderRadius: 'unset',
                                position: 'relative',
                              }}
                            />
                          </Tooltip>
                        </div>
                      );
                    }
                  })}
                </Box>
              </>
            )}
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl
                  component='fieldset'
                  error={Boolean(errors.isApproved && touched.isApproved)}
                >
                  <FormLabel id='demo-radio-buttons-group-label'>
                    Account Status
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby='demo-radio-buttons-group-label'
                    name='radio-buttons-group'
                    sx={{ flexDirection: 'row' }}
                  >
                    <FormControlLabel
                      value='female'
                      control={
                        <Radio
                          color='primary'
                          name='isApproved'
                          onChange={() => {
                            setHasSelectedRadio(true);
                            setFieldValue('isApproved', true);
                            setFieldValue('rejectionReason', ''); // Clear rejection reason
                            setSelectedReasonIds([]);
                          }}
                        />
                      }
                      label='Approve'
                    />
                    <FormControlLabel
                      value='male'
                      control={
                        <Radio
                          color='primary'
                          name='isApproved'
                          onChange={() => {
                            setHasSelectedRadio(true);
                            setFieldValue('isApproved', false);
                            setFieldValue('tradeLicenseExpirationDate', null); // Clear expiration date
                            setFieldValue('brokerLicenseExpirationDate', null); // Clear broker expiration date
                          }}
                        />
                      }
                      label='Reject'
                    />
                  </RadioGroup>
                  {Boolean(errors.isApproved && touched.isApproved) && (
                    <FormHelperText>{errors.isApproved}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {hasSelectedRadio && values?.isApproved === true && (
                <Grid
                  item
                  xs={12}
                  sm={8}
                  md={6}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={actionUser?.userRole === 'broker' ? 'Broker License Expiration Date' : 'Company License Expiration Date'}
                      value={actionUser?.userRole === 'broker' ? values.brokerLicenseExpirationDate : values.tradeLicenseExpirationDate}
                      onChange={(newValue) => {
                        if (actionUser?.userRole === 'broker') {
                          setFieldValue('brokerLicenseExpirationDate', newValue);
                        } else {
                          setFieldValue('tradeLicenseExpirationDate', newValue);
                        }
                      }}
                      minDate={new Date()} // Prevent selecting past dates
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={Boolean(
                            (actionUser?.userRole === 'broker' 
                              ? (errors.brokerLicenseExpirationDate && touched.brokerLicenseExpirationDate)
                              : (errors.tradeLicenseExpirationDate && touched.tradeLicenseExpirationDate))
                          )}
                          helperText={
                            Boolean(
                              (actionUser?.userRole === 'broker' 
                                ? (errors.brokerLicenseExpirationDate && touched.brokerLicenseExpirationDate)
                                : (errors.tradeLicenseExpirationDate && touched.tradeLicenseExpirationDate))
                            ) && (actionUser?.userRole === 'broker' 
                              ? errors?.brokerLicenseExpirationDate 
                              : errors?.tradeLicenseExpirationDate)
                          }
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              )}
              {hasSelectedRadio && values?.isApproved === false && (
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  {rejectionReasonOptions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant='subtitle2' sx={{ mb: 1 }}>
                        Predefined rejection reasons (EN / AR) — select one or more
                      </Typography>
                      <FormGroup>
                        {rejectionReasonOptions.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            control={
                              <Checkbox
                                checked={selectedReasonIds.includes(option.id)}
                                onChange={(event) => {
                                  const nextSelected = event.target.checked
                                    ? [...selectedReasonIds, option.id]
                                    : selectedReasonIds.filter((id) => id !== option.id);
                                  setSelectedReasonIds(nextSelected);
                                  setFieldValue(
                                    'rejectionReason',
                                    appendSelectedReasonsToText(
                                      values.rejectionReason || '',
                                      nextSelected,
                                      rejectionReasonOptions
                                    )
                                  );
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant='body2'>{option.en}</Typography>
                                <Typography variant='body2' color='text.secondary' dir='rtl'>
                                  {option.ar}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedReasonIds.map((id) => {
                          const option = rejectionReasonOptions.find((item) => item.id === id);
                          return option ? (
                            <Chip key={id} size='small' label={option.en} />
                          ) : null;
                        })}
                      </Box>
                    </Box>
                  )}
                  <TextField
                    multiline
                    rows={5}
                    fullWidth
                    label={'Rejection Reason'}
                    name='rejectionReason'
                    id='rejectionReason'
                    onChange={handleChange}
                    value={values.rejectionReason}
                    error={Boolean(
                      errors.rejectionReason && touched.rejectionReason
                    )}
                    helperText={
                      Boolean(
                        errors.rejectionReason && touched.rejectionReason
                      ) && errors?.rejectionReason
                    }
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                resetForm();
                onClose();
                setHasSelectedRadio(false);
              }}
              variant='outlined'
            >
              {'Cancel'}
            </Button>
            <LoadingButton
              type='submit'
              loading={isSubmitting}
              variant='contained'
            >
              {'Save'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <ImageViewer
        open={openLightBox}
        onClose={() => setOpenLightBox(false)}
        imageUrl={attachments?.[activeLightBoxIndex] ? getAttachmentAssetUrl(attachments[activeLightBoxIndex], accessToken) : ''}
        alt={attachments?.[activeLightBoxIndex]?.fileName || 'Image'}
        title={attachments?.[activeLightBoxIndex]?.fileName}
        hasNavigation={attachments?.length > 1}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      />
    </>
  );
};

export default UsersActionModal;
